//! Geometry tests for chart hover hit regions. Regions are produced by
//! core's chart layout engine in chart-local pixels (see `ChartHitRegion` /
//! `HitShape` in the core types); the client only needs point-in-shape tests.

import type { HitShape } from '@/app/quadratic-core-types';

const TAU = Math.PI * 2;

/** Squared distance from a point to a line segment. */
function distanceToSegmentSquared(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lengthSquared = dx * dx + dy * dy;
  let t = lengthSquared === 0 ? 0 : ((px - x1) * dx + (py - y1) * dy) / lengthSquared;
  t = Math.max(0, Math.min(1, t));
  const cx = x1 + t * dx;
  const cy = y1 + t * dy;
  return (px - cx) * (px - cx) + (py - cy) * (py - cy);
}

/** Whether a chart-local point hits a region's shape. */
export function hitsShape(shape: HitShape, x: number, y: number): boolean {
  switch (shape.type) {
    case 'Rect':
      return x >= shape.x && x <= shape.x + shape.w && y >= shape.y && y <= shape.y + shape.h;

    case 'Circle': {
      const dx = x - shape.cx;
      const dy = y - shape.cy;
      return dx * dx + dy * dy <= shape.radius * shape.radius;
    }

    case 'PieSlice': {
      const dx = x - shape.cx;
      const dy = y - shape.cy;
      const r = Math.hypot(dx, dy);
      if (r > shape.radius || r < shape.inner_radius) return false;
      // angles are radians clockwise from 12 o'clock (Excel's convention)
      const theta = Math.atan2(dx, -dy);
      const sweep = shape.end_angle - shape.start_angle;
      const offset = (((theta - shape.start_angle) % TAU) + TAU) % TAU;
      return offset <= sweep;
    }

    case 'Polyline': {
      const toleranceSquared = shape.tolerance * shape.tolerance;
      const points = shape.points;
      for (let i = 0; i + 3 < points.length; i += 2) {
        if (
          distanceToSegmentSquared(x, y, points[i], points[i + 1], points[i + 2], points[i + 3]) <= toleranceSquared
        ) {
          return true;
        }
      }
      return false;
    }

    case 'Polygon': {
      // even-odd ray cast; points is a closed flat [x0,y0,x1,y1,...]
      const points = shape.points;
      if (points.length < 6) return false;
      let inside = false;
      const n = points.length;
      for (let i = 0, j = n - 2; i < n; j = i, i += 2) {
        const xi = points[i];
        const yi = points[i + 1];
        const xj = points[j];
        const yj = points[j + 1];
        const startAbove = yi > y;
        const endAbove = yj > y;
        if (startAbove !== endAbove && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
          inside = !inside;
        }
      }
      return inside;
    }
  }
}
